import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Download, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import Certificate from "@/components/formations/Certificate";

export default function CertificatePage() {
  const { number } = useParams();
  const [cert, setCert] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound
  const [downloading, setDownloading] = useState(false);
  const [scale, setScale] = useState(1);
  const wrapRef = useRef(null);
  const captureRef = useRef(null);

  useEffect(() => {
    let active = true;
    api.get(`/certificates/${number}`)
      .then(({ data }) => { if (active) { setCert(data); setStatus("ok"); } })
      .catch(() => { if (active) setStatus("notfound"); });
    return () => { active = false; };
  }, [number]);

  useEffect(() => {
    const onResize = () => {
      const w = wrapRef.current?.clientWidth || 1000;
      setScale(Math.min(1, w / 1000));
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [cert]);

  const download = async () => {
    if (!captureRef.current) return;
    setDownloading(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      if (document.fonts?.ready) await document.fonts.ready;
      await new Promise((r) => setTimeout(r, 250));
      const canvas = await html2canvas(captureRef.current, {
        scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false,
        windowWidth: 1000, windowHeight: 707,
      });
      const img = canvas.toDataURL("image/jpeg", 0.95);
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      pdf.addImage(img, "JPEG", 0, 0, pw, ph);
      pdf.save(`certificat-${cert.certificate_number}.pdf`);
    } catch (e) {
      toast.error(apiError(e, "Téléchargement impossible"));
    } finally {
      setDownloading(false);
    }
  };

  if (status === "loading") {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  }
  if (status === "notfound") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 px-6 text-center" data-testid="cert-notfound">
        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-50"><ShieldAlert className="h-8 w-8 text-rose-500" /></span>
        <h1 className="font-display text-2xl font-extrabold text-ink">Certificat introuvable</h1>
        <p className="max-w-md text-sm text-slate-500">Le numéro « {number} » ne correspond à aucun certificat ISMO-TECH.</p>
        <Link to="/" className="rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white">Retour à l'accueil</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-6" data-testid="certificate-page">
      <div className="mx-auto max-w-[1040px] px-3 sm:px-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <Link to="/compte/formations" data-testid="cert-back" className="flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-ink hover:border-brand hover:text-brand">
            <ArrowLeft className="h-4 w-4" /> Retour
          </Link>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700" data-testid="cert-verified">
              <BadgeCheck className="h-4 w-4" /> Certificat authentique
            </span>
            <button onClick={download} disabled={downloading} data-testid="cert-download"
              className="flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-amber-600 disabled:opacity-60">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {downloading ? "Génération..." : "Télécharger PDF"}
            </button>
          </div>
        </div>

        <div ref={wrapRef} className="w-full">
          <div style={{ height: 707 * scale }}>
            <div style={{ width: 1000, height: 707, transform: `scale(${scale})`, transformOrigin: "top left" }}>
              <Certificate ref={captureRef} data={cert} />
            </div>
          </div>
        </div>

        <p className="mt-5 text-center text-xs text-slate-500">
          Émis par <span className="font-bold text-ink">{cert.issuer_name}</span> · N° {cert.certificate_number} · Vérifié par ISMO-TECH
        </p>
      </div>
    </div>
  );
}
